import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { ArrowLeft, Send, User } from 'lucide-react';

export default function NegotiationChatPage() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [counterAmount, setCounterAmount] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [bidId]);

  const fetchData = async () => {
    try {
      await Promise.all([fetchBidDetails(), fetchMessages()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBidDetails = async () => {
    // Get bid details from vendor bids or rfp bids
    try {
      const rfps = await api.getRFPs();
      for (const rfp of rfps.data) {
        const bidsRes = await api.getRFPBids(rfp.id);
        const foundBid = bidsRes.data.find(b => b.id === bidId);
        if (foundBid) {
          setBid(foundBid);
          break;
        }
      }
    } catch (error) {
      console.error('Failed to fetch bid details');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.getBidNegotiations(bidId);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await api.createNegotiation({
        bid_id: bidId,
        message: newMessage,
        counter_amount: counterAmount ? parseFloat(counterAmount) : null,
        modifications: {}
      });
      setNewMessage('');
      setCounterAmount('');
      fetchMessages();
      toast.success('Message sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto mb-4"></div>
          <p className="text-slate-800">Loading negotiation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 sm:mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Bids
        </Button>

        {bid && (
          <Card className="mb-4 sm:mb-6 border-2 border-navy-200">
            <CardHeader className="bg-gradient-to-r from-navy-600 to-navy-800 text-white p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl">Negotiating with {bid.vendor_name || 'Vendor'}</CardTitle>
              <div className="text-navy-100 mt-2 text-sm sm:text-base">
                {bid.property_name || 'Property'} - Original Quote: ₹{bid.quoted_amount.toLocaleString()}
              </div>
            </CardHeader>
          </Card>
        )}

        <Card className="border-2 border-navy-200">
          <CardHeader>
            <CardTitle>Negotiation Chat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6 max-h-[400px] sm:max-h-[500px] overflow-y-auto p-4 bg-navy-50 rounded-lg">
              {messages.length === 0 ? (
                <div className="text-center text-slate-700 py-8 text-sm sm:text-base">
                  No messages yet. Start the conversation below!
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'client' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[70%] rounded-lg p-3 sm:p-4 text-sm sm:text-base ${
                        msg.sender_role === 'client'
                          ? 'bg-navy-600 text-white'
                          : 'bg-white border-2 border-navy-200 text-navy-800'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {msg.sender_name} ({msg.sender_role})
                        </span>
                      </div>
                      <p className="mb-2">{msg.message}</p>
                      {msg.counter_amount && (
                        <div className="mt-2 pt-2 border-t border-navy-300">
                          <span className="text-sm font-semibold">
                            Counter Offer: ₹{msg.counter_amount.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="text-xs opacity-75 mt-2">
                        {new Date(msg.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Your Message</label>
                <Textarea
                  required
                  rows={3}
                  placeholder="Type your message here... (e.g., Can you reduce the price? Can you add breakfast?)"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Counter Offer Amount (Optional)</label>
                <Input
                  type="number"
                  placeholder="e.g., 240000"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(e.target.value)}
                />
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  ⚠️ All negotiations are logged. Please use platform chat only. Sharing contact information is prohibited.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-navy-600 hover:bg-navy-700"
              >
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}